package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.roster.AbsenceDto
import com.evoapps.evoshift.data.dto.roster.AbsenceStatusDto
import com.evoapps.evoshift.data.dto.roster.AbsenceTypeDto
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.CareRecipient
import com.evoapps.evoshift.domain.model.LimitUnit
import com.evoapps.evoshift.domain.model.OccasionReason

class AbsenceMapper(
    private val metadataMapper: MetadataMapper
) {

    fun toDomain(dto: AbsenceDto, idFromSnapshot: String? = null): Absence {
        return Absence(
            id = idFromSnapshot ?: dto.id,
            userId = dto.userId,
            workplaceId = dto.workplaceId,
            type = typeOf(dto.type, dto.kind),
            status = AbsenceStatus.entries.firstOrNull { it.name == dto.status } ?: AbsenceStatus.PENDING,
            startDate = dto.startDate,
            endDate = dto.endDate,
            reason = dto.reason,
            attachmentUrl = dto.attachmentUrl,
            processedBy = dto.processedBy,
            processedAt = dto.processedAt,
            metadata = metadataMapper.toDomain(dto.metadata),
            sickPayFull = dto.sickPayFull,
            rejectionReason = dto.rejectionReason,
            startTime = dto.startTime?.takeIf { it.isNotBlank() && !dto.endTime.isNullOrBlank() },
            endTime = dto.endTime?.takeIf { it.isNotBlank() && !dto.startTime.isNullOrBlank() },
            occasion = OccasionReason.entries.firstOrNull { it.name == dto.occasion },
            careFor = CareRecipient.entries.firstOrNull { it.name == dto.careFor },
            limitUnit = LimitUnit.entries.firstOrNull { it.name == dto.limitUnit },
            children = dto.children,
            compensatedOvertimeHours = dto.compensatedOvertimeHours,
            overrideNote = dto.overrideNote
        )
    }

    fun toDto(domain: Absence): AbsenceDto {
        val legacy = legacyType(domain.type)
        return AbsenceDto(
            id = domain.id,
            userId = domain.userId,
            workplaceId = domain.workplaceId,
            type = legacy.name,
            status = when (domain.status) {
                AbsenceStatus.PENDING -> AbsenceStatusDto.PENDING
                AbsenceStatus.APPROVED -> AbsenceStatusDto.APPROVED
                AbsenceStatus.REJECTED -> AbsenceStatusDto.REJECTED
            }.name,
            startDate = domain.startDate,
            endDate = domain.endDate,
            reason = domain.reason,
            attachmentUrl = domain.attachmentUrl,
            processedBy = domain.processedBy,
            processedAt = domain.processedAt,
            metadata = metadataMapper.toDto(domain.metadata),
            sickPayFull = domain.sickPayFull,
            rejectionReason = domain.rejectionReason,
            kind = domain.type.name.takeIf { it != legacy.name },
            startTime = domain.startTime.takeIf { domain.isHourly },
            endTime = domain.endTime.takeIf { domain.isHourly },
            occasion = domain.occasion?.name,
            careFor = domain.careFor?.name,
            limitUnit = domain.limitUnit?.name,
            children = domain.children,
            compensatedOvertimeHours = domain.compensatedOvertimeHours,
            overrideNote = domain.overrideNote
        )
    }

    companion object {
        /**
         * SH-223: the `type` an app before SH-223 reads for [type]: itself for the four it knows;
         * quarantine and isolation as sick leave (they are paid as sickness, as those apps then
         * estimate); every other new type as force majeure, which those apps treat as a day away
         * (they free the shifts and do not schedule the person) without paying it or taking it
         * from the annual leave. Never VACATION or ON_DEMAND: those apps would count the days
         * against the person's annual leave.
         */
        fun legacyType(type: AbsenceType): AbsenceTypeDto = when (type) {
            AbsenceType.SICK_LEAVE, AbsenceType.QUARANTINE -> AbsenceTypeDto.SICK_LEAVE
            AbsenceType.VACATION -> AbsenceTypeDto.VACATION
            AbsenceType.ON_DEMAND -> AbsenceTypeDto.ON_DEMAND
            AbsenceType.FORCE_MAJEURE, AbsenceType.OCCASIONAL_LEAVE, AbsenceType.BLOOD_DONATION, AbsenceType.TIME_OFF_IN_LIEU,
            AbsenceType.CHILDCARE, AbsenceType.CARER_LEAVE, AbsenceType.CARE_ALLOWANCE, AbsenceType.UNPAID_LEAVE,
            AbsenceType.MATERNITY, AbsenceType.PATERNITY, AbsenceType.PARENTAL, AbsenceType.CHILDCARE_LEAVE -> AbsenceTypeDto.FORCE_MAJEURE
        }

        /**
         * The precise type: [kind] when this version knows it, else the stored [type]; a [kind]
         * from a later version falls back to its legacy [type] (still away that day), and an
         * unreadable [type] to force majeure (away, unpaid here).
         */
        fun typeOf(type: String, kind: String?): AbsenceType =
            AbsenceType.fromName(kind) ?: AbsenceType.fromName(type)?.takeIf { it.name in LEGACY_NAMES } ?: AbsenceType.FORCE_MAJEURE

        private val LEGACY_NAMES = AbsenceTypeDto.entries.map { it.name }.toSet()
    }
}
